'use client';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useOrgContext } from '@organizations/ui/OrgContext';
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Command,
} from '@core/ui/primitives/command';
import { CommandInput } from '@core/ui/primitives/command/CommandInput';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@core/ui/primitives/dialog';
import { Badge } from '@core/ui/primitives/badge';
import { Spinner } from '@core/ui/primitives/spinner';
import { Kbd } from '@core/ui/primitives/kbd';
import { Button } from '@/core/ui/primitives/button';
import { Switch } from '@/core/ui/primitives/switch';
import { FieldLabel } from '@core/ui/primitives/field';
import { cn } from '@core/helpers/misc/GeneralUtils';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@core/ui/primitives/dropdown-menu';
import { LuSearch, LuMic, LuSquareStack, LuMessagesSquare, LuCommand, LuMap, LuPencilRuler, LuUser, LuSettings, LuPlug } from 'react-icons/lu';
import { searchClient } from '@search/ui/SearchClient';
import type { UiSearchResult } from '@search/core/SearchResultMapper';
import { useAudioTranscriptionFeature } from '@audio-transcription';
import { SearchResults } from '@search/ui/SearchResults';
import { SearchResultsGrouped } from '@search/ui/SearchResultsGrouped';
import type { UiGroupedResult } from '@search/core/SearchTypes';
import { defaultCommandCenterConfig } from '../core/CommandCenterConfig';
import type {
  CommandAvailability,
  CommandCenterMode,
  CommandDefinition,
  CommandVaultEntry,
} from '../core/CommandCenterTypes';
interface CommandCenterContextValue {
  isOpen: boolean;
  mode: CommandCenterMode;
  query: string;
  open: () => void;
  close: () => void;
  toggle: () => void;
  openSearchMode: () => void;
}
const CommandCenterContext = createContext<CommandCenterContextValue | null>(null);
function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = (el.tagName || '').toUpperCase();
  const editable = (el as any).isContentEditable === true;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || editable;
}
async function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}
export function useCommandCenter() {
  const ctx = useContext(CommandCenterContext);
  if (!ctx) {
    throw new Error('useCommandCenter must be used within CommandCenterProvider');
  }
  return ctx;
}
interface CommandCenterProviderProps {
  children: React.ReactNode;
}
export function CommandCenterProvider({ children }: CommandCenterProviderProps) {
  const semanticSwitchId = React.useId();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<CommandCenterMode>('commands');
  const [commandSearch, setCommandSearch] = useState('');
  const [vaultSearch, setVaultSearch] = useState('');
  const [vaults, setVaults] = useState<CommandVaultEntry[]>([]);
  const [searchResults, setSearchResults] = useState<UiSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedVaultIds, setSelectedVaultIds] = useState<string[]>([]);
  const searchRequestRef = useRef(0);
  const [useSemanticSearch, setUseSemanticSearch] = useState(false);
  const router = useRouter();
  const searchTimerRef = useRef<number | null>(null);

  // Get validated org context from OrgContextProvider (set by org vault layout)
  const { orgSlug: currentOrgSlug } = useOrgContext();
  const {
    isAvailable: canTranscribe,
    toggle: toggleTranscription,
    start: startTranscription,
  } = useAudioTranscriptionFeature();
  const config = defaultCommandCenterConfig;
  const close = useCallback(() => setIsOpen(false), []);
  const open = useCallback(() => {
    setMode('commands');
    setIsOpen(true);
  }, []);
  const openSearchMode = useCallback(() => {
    setMode('search');
    setIsOpen(true);
  }, []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  // Reset state when closing (but keep last mode until next open to avoid flicker)
  useEffect(() => {
    if (!isOpen) {
      setCommandSearch('');
      setVaultSearch('');
      setSearchResults([]);
      setIsSearching(false);
      setSelectedIndex(0);
    }
  }, [isOpen]);
  // Respond to global hotkey events
  useEffect(() => {
    const onToggle = () => {
      if (!isOpen) {
        open();
        return;
      }
      // If already open in command list mode, act as a true toggle and close.
      if (mode === 'commands') {
        close();
        return;
      }
      // If open in search mode, switch back to command list without closing.
      setMode('commands');
      setVaultSearch('');
      setSearchResults([]);
      setIsSearching(false);
      setSelectedIndex(0);
    };
    const onSearch = () => {
      if (isOpen && mode === 'search') {
        close();
      } else {
        openSearchMode();
      }
    };
    const onMindmap = () => {
      if (isOpen) {
        close();
      }
    };
    const onExcalidraw = () => {
      if (isOpen) {
        close();
      }
    };
    const onFeedback = () => {
      if (isOpen) {
        close();
      }
    };
    const onAudio = () => {
      if (isOpen) {
        close();
      }
    };
    window.addEventListener('hotkey:command.center.toggle', onToggle);
    window.addEventListener('hotkey:command.center.search', onSearch);
    window.addEventListener('hotkey:mindmap.toggle', onMindmap);
    window.addEventListener('hotkey:excalidraw.toggle', onExcalidraw);
    window.addEventListener('hotkey:feedback.toggle', onFeedback);
    window.addEventListener('hotkey:audio.toggle', onAudio);
    return () => {
      window.removeEventListener('hotkey:command.center.toggle', onToggle);
      window.removeEventListener('hotkey:command.center.search', onSearch);
      window.removeEventListener('hotkey:mindmap.toggle', onMindmap);
      window.removeEventListener('hotkey:excalidraw.toggle', onExcalidraw);
      window.removeEventListener('hotkey:feedback.toggle', onFeedback);
      window.removeEventListener('hotkey:audio.toggle', onAudio);
    };
  }, [close, isOpen, mode, open, openSearchMode]);
  // Lazy-load accessible vaults when opening
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    void (async () => {
      try {
        const response = await fetch('/api/vaults/me', {
          headers: { Accept: 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) return;
        const payload = (await response.json()) as {
          organizations?: Array<{
            slug: string;
            name?: string;
            vaults: Array<{
              id: string;
              slug: string;
              title: string;
              href?: string;
            }>;
          }>;
        };
        const mapped: CommandVaultEntry[] = (payload.organizations ?? []).flatMap((org) =>
          (org.vaults ?? [])
            .filter((v) => Boolean(v.href))
            .map((v) => ({
              id: v.id,
              title: v.title,
              href: v.href!,
              slug: v.slug,
              organizationSlug: org.slug,
              organizationName: org.name ?? org.slug,
            }))
        );
        if (active) {
          setVaults(mapped);
        }
      } catch (err) {
        console.warn('⚠️  Failed to load vault list for command center', err);
      }
    })();
    return () => {
      active = false;
    };
  }, [isOpen]);
  const navigate = useCallback(
    (href: string) => {
      close();
      requestAnimationFrame(() => router.push(href));
    },
    [close, router]
  );
  const runSearch = useCallback(
    async (value: string) => {
      const requestId = ++searchRequestRef.current;
      const trimmed = value.trim();
      if (!trimmed || trimmed.length < config.minQueryLength) {
        if (requestId === searchRequestRef.current) {
          setIsSearching(false);
        }
        return;
      }
      setIsSearching(true);
      const start = Date.now();
      try {
        const { results, error } = await searchClient.search({
          query: trimmed,
          limit: config.maxResults,
          grouped: true,
          maxMatchesPerPage: 4,
          scope: 'public',
          useSemantic: useSemanticSearch,
          org: currentOrgSlug,
        });
        if (requestId === searchRequestRef.current) {
          if (error) {
            // Log error but don't clear results - let user retry
            console.warn('Command center search error:', error.message);
          }
          const filtered =
            selectedVaultIds.length === 0
              ? (results as any[])
              : (results as any[]).filter((result) => {
                  const r = result as any;
                  const id = (r as any).vaultId ?? (r as UiGroupedResult).vaultId;
                  return typeof id === 'string' && selectedVaultIds.includes(id);
                });
          setSearchResults(filtered as any);
        }
      } catch (error) {
        console.error('Command center search failed', error);
        if (requestId === searchRequestRef.current) {
          // Keep previous results; only stop the spinner
          setIsSearching(false);
        }
        return;
      } finally {
        const elapsed = Date.now() - start;
        if (requestId === searchRequestRef.current) {
          if (elapsed < config.minimumSpinnerMs) {
            await delay(config.minimumSpinnerMs - elapsed);
          }
          setIsSearching(false);
        }
      }
    },
    [config.maxResults, config.minQueryLength, config.minimumSpinnerMs, useSemanticSearch, selectedVaultIds, currentOrgSlug]
  );
  // Keep selected index in range when results change
  useEffect(() => {
    if (!Array.isArray(searchResults) || searchResults.length === 0) {
      setSelectedIndex(0);
      return;
    }
    setSelectedIndex((prev) => Math.max(0, Math.min(prev, searchResults.length - 1)));
  }, [searchResults]);
  const handleInputChange = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      const meetsMinLength = trimmed.length >= config.minQueryLength;
      if (mode === 'search') {
        setVaultSearch(value);
      } else {
        setCommandSearch(value);
      }
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current);
      }
      if (mode !== 'search' || !meetsMinLength) {
        setIsSearching(false);
        return;
      }
      searchTimerRef.current = window.setTimeout(() => {
        void runSearch(value);
      }, config.searchDebounceMs);
    },
    [config.minQueryLength, config.searchDebounceMs, mode, runSearch]
  );
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current);
      }
    };
  }, []);
  const availabilityLabel = useCallback((availability: CommandAvailability | undefined) => {
    if (availability === 'pro') return 'Pro';
    if (availability === 'disabled') return 'Unavailable';
    return null;
  }, []);
  const query = mode === 'search' ? vaultSearch : commandSearch;
  const vaultGroups = useMemo(() => {
    if (!vaults.length) return [];
    const byOrg = new Map<
      string,
      {
        organizationName: string;
        organizationSlug: string;
        vaults: CommandVaultEntry[];
      }
    >();
    for (const vault of vaults) {
      const key = vault.organizationSlug;
      const existing = byOrg.get(key);
      if (existing) {
        existing.vaults.push(vault);
      } else {
        byOrg.set(key, {
          organizationName: vault.organizationName,
          organizationSlug: vault.organizationSlug,
          vaults: [vault],
        });
      }
    }
    return Array.from(byOrg.values())
      .map((group) => ({
        ...group,
        vaults: [...group.vaults].sort((a, b) => a.title.localeCompare(b.title)),
      }))
      .sort((a, b) => a.organizationName.localeCompare(b.organizationName));
  }, [vaults]);
  const selectedVaultLabel = useMemo(() => {
    if (!selectedVaultIds.length) return 'All vaults';
    if (selectedVaultIds.length === 1) {
      const id = selectedVaultIds[0];
      const match = vaults.find((v) => v.id === id);
      return match?.title ?? '1 vault';
    }
    return `${selectedVaultIds.length} vaults`;
  }, [selectedVaultIds, vaults]);
  // Build registry on the fly so availability is always fresh
  const commands = useMemo<CommandDefinition[]>(() => {
    const items: CommandDefinition[] = [
      {
        id: 'search',
        label: 'Search your documents',
        keywords: ['find', 'lookup'],
        shortcut: { keys: ['meta', 'alt', 'k'] },
        run: () => {
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('hotkey:command.center.search'));
            }
          } catch {
            // fall back to local behaviour if dispatch fails
            setMode('search');
          }
          // Preserve vaultSearch content; no immediate search, and keep palette open
          return false;
        },
      },
      {
        id: 'mindmap',
        label: 'Open mindmap',
        keywords: ['graph', 'map', 'canvas'],
        shortcut: { keys: ['meta', 'm'] },
        run: () => {
          try {
            window.dispatchEvent(new CustomEvent('mindmap:open'));
          } catch {}
          close();
          return true;
        },
      },
      {
        id: 'excalidraw',
        label: 'Open Excalidraw',
        keywords: ['draw', 'diagram', 'whiteboard', 'sketch', 'canvas'],
        shortcut: { keys: ['meta', 'e'] },
        run: () => {
          try {
            window.dispatchEvent(new CustomEvent('excalidraw:open'));
          } catch {}
          close();
          return true;
        },
      },
      {
        id: 'dictate',
        label: 'Start dictation',
        keywords: ['voice', 'transcription', 'audio'],
        shortcut: { keys: ['meta', 'alt', 'r'] },
        availability: canTranscribe ? 'available' : 'pro',
        badge: canTranscribe ? undefined : 'Pro',
        run: () => {
          if (!canTranscribe) return false;
          if (typeof toggleTranscription === 'function') {
            toggleTranscription();
          } else if (typeof startTranscription === 'function') {
            void startTranscription();
          }
          close();
          return true;
        },
      },
      {
        id: 'feedback',
        label: 'Send feedback',
        keywords: ['bug', 'idea'],
        shortcut: { keys: ['meta', 'shift', 'f'] },
        run: () => {
          try {
            window.dispatchEvent(new CustomEvent('feedback:open'));
          } catch {}
          close();
          return true;
        },
      },
      {
        id: 'profile-settings',
        label: 'Open profile settings',
        keywords: ['profile', 'settings', 'account'],
        run: () => {
          window.dispatchEvent(new CustomEvent('profile.settings.open'));
          close();
          return true;
        },
      },
      {
        id: 'organization-settings',
        label: 'Open organization settings',
        keywords: ['organization', 'org', 'settings'],
        run: () => {
          window.dispatchEvent(new CustomEvent('organization.settings.open'));
          close();
          return true;
        },
      },
      {
        id: 'configure-mcp',
        label: 'Configure MCP agent',
        keywords: ['mcp', 'agent', 'token', 'api', 'access', 'cursor', 'claude', 'windsurf', 'cline'],
        run: () => {
          window.dispatchEvent(new CustomEvent('profile.settings.open', { detail: { section: 'tokens' } }));
          close();
          return true;
        },
      },
    ];
    return items;
  }, [canTranscribe, close, navigate, startTranscription, toggleTranscription]);
  const renderCommandIcon = useCallback((id: string) => {
    if (id === 'search') return <LuSearch className='h-4 w-4 text-muted-foreground' />;
    if (id === 'mindmap') return <LuMap className='h-4 w-4 text-muted-foreground' />;
    if (id === 'excalidraw') return <LuPencilRuler className='h-4 w-4 text-muted-foreground' />;
    if (id === 'dictate') return <LuMic className='h-4 w-4 text-muted-foreground' />;
    if (id === 'feedback') return <LuMessagesSquare className='h-4 w-4 text-muted-foreground' />;
    if (id === 'profile-settings') return <LuUser className='h-4 w-4 text-muted-foreground' />;
    if (id === 'organization-settings') return <LuSettings className='h-4 w-4 text-muted-foreground' />;
    if (id === 'configure-mcp') return <LuPlug className='h-4 w-4 text-muted-foreground' />;
    if (id.startsWith('vault:')) return <LuSquareStack className='h-4 w-4 text-muted-foreground' />;
    return <LuCommand className='h-4 w-4 text-muted-foreground' />;
  }, []);
  const contextValue: CommandCenterContextValue = useMemo(
    () => ({
      isOpen,
      mode,
      query,
      open,
      close,
      toggle,
      openSearchMode,
    }),
    [close, isOpen, mode, open, openSearchMode, query, toggle]
  );
  const handleCommandSelect = useCallback(
    async (command: CommandDefinition) => {
      if (command.availability === 'disabled' || command.availability === 'pro') {
        return;
      }
      const result = await command.run();
      if (result !== false) {
        close();
      }
    },
    [close]
  );
  return (
    <CommandCenterContext.Provider value={contextValue}>
      {children}
      <Dialog open={isOpen} onOpenChange={(nextOpen) => (!nextOpen ? close() : open())}>
        <DialogContent
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
          onEscapeKeyDown={(event) => {
            if (mode === 'search') {
              event.preventDefault();
              setMode('commands');
              setVaultSearch('');
              setSearchResults([]);
              setIsSearching(false);
              setSelectedIndex(0);
            }
          }}
          className={cn(
            'w-full border border-border bg-background p-0 shadow-2xl transition-[max-width] duration-200 ease-out',
            // Remove default translate and zoom animations - using inline style transform instead
            '!translate-x-0 !translate-y-0 data-[state=closed]:zoom-out-0 data-[state=open]:zoom-in-0',
            mode === 'search' ? 'sm:max-w-2xl' : 'sm:max-w-md'
          )}>
          <DialogTitle className='sr-only'>Command center</DialogTitle>
          <DialogDescription className='sr-only'>Search and run commands</DialogDescription>
          <Command className='rounded-2xl border-none shadow-none'>
            <CommandInput
              leading={<LuSearch className='h-4 w-4 text-muted-foreground' />}
              trailing={
                mode === 'search' ? (
                  <div className='flex items-center gap-2'>
                    {isSearching ? <Spinner className='h-4 w-4' /> : null}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          className='gap-1 px-2 py-0 text-xs focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30'>
                          <LuSquareStack className='h-3 w-3 text-muted-foreground' />
                          <span className='truncate text-xs text-foreground'>{selectedVaultLabel}</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end' className='w-56'>
                        <DropdownMenuLabel className='text-xs text-muted-foreground'>Vault filters</DropdownMenuLabel>
                        <DropdownMenuItem
                          onSelect={(event) => {
                            event.preventDefault();
                            setSelectedVaultIds([]);
                          }}
                          className='text-xs'>
                          All vaults
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {vaultGroups.map((group, groupIndex) => (
                          <div key={group.organizationSlug}>
                            <DropdownMenuLabel className='text-[11px] text-muted-foreground'>
                              {group.organizationName}
                            </DropdownMenuLabel>
                            {group.vaults.map((vault) => (
                              <DropdownMenuCheckboxItem
                                key={vault.id}
                                checked={selectedVaultIds.includes(vault.id)}
                                onCheckedChange={(checked) => {
                                  setSelectedVaultIds((prev) => {
                                    if (checked) {
                                      if (prev.includes(vault.id)) return prev;
                                      return [...prev, vault.id];
                                    }
                                    return prev.filter((id) => id !== vault.id);
                                  });
                                }}
                                onSelect={(event) => {
                                  event.preventDefault();
                                }}
                                className='pl-6 text-xs'>
                                {vault.title}
                              </DropdownMenuCheckboxItem>
                            ))}
                            {groupIndex < vaultGroups.length - 1 ? <DropdownMenuSeparator /> : null}
                          </div>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <div className='flex items-center gap-1 pl-1 border-l border-border/60'>
                      <Switch
                        id={semanticSwitchId}
                        checked={useSemanticSearch}
                        onCheckedChange={(next) => setUseSemanticSearch(Boolean(next))}
                        aria-labelledby={`${semanticSwitchId}-label`}
                      />
                      <FieldLabel
                        id={`${semanticSwitchId}-label`}
                        htmlFor={semanticSwitchId}
                        className='text-[11px] font-medium text-muted-foreground'>
                        Semantic
                      </FieldLabel>
                    </div>
                  </div>
                ) : null
              }
              placeholder={mode === 'search' ? 'Search across vaults…' : 'Type a command or search…'}
              onValueChange={handleInputChange}
              onKeyDown={(e) => {
                if (mode === 'search') {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    if (searchResults.length > 0) {
                      setSelectedIndex((prev) => (prev + 1) % searchResults.length);
                    }
                    return;
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    if (searchResults.length > 0) {
                      setSelectedIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
                    }
                    return;
                  }
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!isSearching && searchResults.length > 0) {
                      const selected = searchResults[selectedIndex] as any;
                      const url = selected?.url || selected?.matches?.[0]?.url || '';
                      if (url) {
                        navigate(url);
                      }
                    }
                    return;
                  }
                }
                if (mode === 'search' && e.key === 'Enter') {
                  e.preventDefault();
                  void runSearch(vaultSearch);
                  return;
                }
              }}
            />
            <CommandList className='max-h-80 overflow-y-auto scrollbar-fade'>
              {mode === 'commands' && commands.length === 0 && <CommandEmpty>No matching commands.</CommandEmpty>}
              {mode === 'search' &&
                !isSearching &&
                searchResults.length === 0 &&
                vaultSearch.trim().length >= config.minQueryLength && (
                  <CommandEmpty>No results. Try a different query.</CommandEmpty>
                )}

              {mode === 'commands' && (
                <CommandGroup heading='Available commands'>
                  {commands.map((command) => (
                    <CommandItem
                      key={command.id}
                      value={`${command.label} ${command.keywords?.join(' ') ?? ''}`}
                      onSelect={() => void handleCommandSelect(command)}
                      aria-disabled={command.availability === 'disabled' || command.availability === 'pro'}
                      className='items-center'>
                      {renderCommandIcon(command.id)}
                      <div className='flex flex-1 items-center justify-between gap-2'>
                        <span className='text-sm font-medium'>{command.label}</span>
                        {command.badge && (
                          <Badge variant='outline' className='px-1.5 py-0.5 text-[11px]'>
                            {command.badge}
                          </Badge>
                        )}
                        {(command.shortcut || availabilityLabel(command.availability)) && (
                          <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                            {command.shortcut ? <Kbd size='sm' keys={command.shortcut.keys} /> : null}
                            {availabilityLabel(command.availability) ? (
                              <span>{availabilityLabel(command.availability)}</span>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {mode === 'search' &&
                searchResults.length > 0 &&
                (Array.isArray(searchResults) &&
                searchResults[0] &&
                'matches' in (searchResults[0] as unknown as Record<string, unknown>) ? (
                  <SearchResultsGrouped
                    results={searchResults as unknown as UiGroupedResult[]}
                    selectedIndex={selectedIndex}
                    onMatchClick={(url) => navigate(url)}
                    query={vaultSearch}
                  />
                ) : (
                  <SearchResults
                    results={searchResults as UiSearchResult[]}
                    selectedIndex={selectedIndex}
                    onResultClick={(result) => {
                      const url = (result as any).url || (result as any)?.matches?.[0]?.url || '';
                      if (url) navigate(url);
                    }}
                    query={vaultSearch}
                  />
                ))}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </CommandCenterContext.Provider>
  );
}
